import React, { useEffect } from 'react'
import AceEditor from 'react-ace'
import { makeStyles } from '@material-ui/core'
import ErrorBoundary from '../../../../../../molecules/ErrorBoundary'
import SydButton from '../../../../../../commonDesign/Button'
import { JsonWithPathsMode } from '../templatedJson'
import { usePipelineWorkspace } from '../PipelineWorkspaceContext'
import ResultPanel from '../ResultPanel'
import PipelineDiffView from '../PipelineDiffView'
import Variables from './Variables'

const useStyles = makeStyles((theme) => ({
  editorPanel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--surface-bg-color-d4)'
  },
  editorContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  editorBody: {
    flex: 1,
    minHeight: 0
  },
  variablesSection: {
    flexShrink: 0,
    borderTop: '1px solid var(--color-neutral-600)',
    maxHeight: '250px',
    overflow: 'auto',
    backgroundColor: 'var(--surface-bg-color-d3)'
  },
  resultsSection: {
    flexShrink: 0,
    borderTop: '1px solid var(--color-neutral-600)',
    maxHeight: '300px',
    overflow: 'auto',
    backgroundColor: 'var(--surface-bg-color-d4)'
  },
  actionsBar: {
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderTop: '1px solid var(--color-neutral-600)',
    backgroundColor: 'var(--surface-bg-color-d2)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1)
  }
}))

function EditorPanel () {
  const classes = useStyles()
  const {
    editorRef,
    setVariables,
    variables,
    handleRun,
    responseFetching,
    requestValidation,
    handleEditorChange,
    pipelineJson,
    response,
    responseError,
    diffMode,
    proposedPipelineJson,
    acceptProposal,
    rejectProposal
  } = usePipelineWorkspace()

  useEffect(() => {
    if (editorRef.current) {
      const session = editorRef.current.editor.getSession()
      session.setMode(new JsonWithPathsMode())
    }
  }, [editorRef])

  const isRunDisabled = responseFetching || (requestValidation && !requestValidation.isValid)

  return (
    <ErrorBoundary name='Code Editor'>
      <div className={classes.editorPanel}>
        <div className={classes.editorContainer}>
          {diffMode ? (
            <>
              <PipelineDiffView
                currentPipeline={pipelineJson}
                proposedPipeline={proposedPipelineJson}
                onAccept={acceptProposal}
                onReject={rejectProposal}
              />
              <div className={classes.variablesSection}>
                <Variables onChange={setVariables} defaultVariables={variables} />
              </div>
            </>
          ) : (
            <>
              <div className={classes.editorBody}>
                <AceEditor
                  ref={editorRef}
                  height='100%'
                  width='100%'
                  mode='json'
                  theme='xcode'
                  name='json-editor'
                  editorProps={{ $blockScroll: true }}
                  value={pipelineJson}
                  onChange={handleEditorChange}
                  tabSize={2}
                />
              </div>
              <div className={classes.variablesSection}>
                <Variables onChange={setVariables} defaultVariables={variables} />
              </div>
              {(response || responseError) && (
                <div className={classes.resultsSection}>
                  <ResultPanel />
                </div>
              )}
            </>
          )}
        </div>
        {!diffMode && (
          <div className={classes.actionsBar}>
            <SydButton
              variant='secondary'
              size='sm'
              onClick={handleRun}
              disabled={isRunDisabled}
              icon='arrowForward'
              processing={responseFetching}
            >
              Run
            </SydButton>
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

export default EditorPanel
